import { forwardRef, type InputHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

const inputVariants = cva(
  [
    'flex h-9 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground shadow-2xs',
    'outline-none transition-colors',
    'placeholder:text-muted-foreground',
    'focus-visible:ring-2 focus-visible:ring-primary/40',
    // Campo com erro (Figma, Input Error=True): borda destructive + anel de 3px
    // destructive-60 sempre visível, com ou sem foco. aria-invalid vem do
    // FormFieldControl, então qualquer FormField com erro já pega o padrão.
    'aria-invalid:border-destructive aria-invalid:shadow-none aria-invalid:ring-[3px] aria-invalid:ring-destructive-60',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
    '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-input',
        destructive: 'border-destructive shadow-none ring-[3px] ring-destructive-60',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof inputVariants> {}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      data-slot="input"
      type={type}
      className={cn(inputVariants({ variant }), className)}
      {...props}
    />
  )
);
Input.displayName = 'Input';

export { Input, inputVariants };
